/* eslint-disable react/jsx-no-constructed-context-values */
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { orderBy, uniq } from 'lodash';
import { timeUnix } from '@/services/utils/k_time';
import { useRestaurantContext } from '@/contexts/restaurant';
import TutorialAPIManager from '../repository';
import { ActionEnum, ApplicationEnum, ITutorial } from '../repository/types';
import { getLSAction, setLSAction } from '../storage';
import { IAction, ITutorialAction } from '../types';
import { ITutorialContext } from './types';

export const checkAction = (data: ITutorialAction, action: ActionEnum) => {
    switch (action) {
        case ActionEnum.Done:
        case ActionEnum.Load:
        case ActionEnum.PaymentSuccess:
        case ActionEnum.View:
            return data.actions.some((o) => o.action === action);
        case ActionEnum.Unload:
        default:
            return true;
    }
};

const TutorialContext = createContext<ITutorialContext>({
    tutorial: undefined,
    hasError: false,
    actions: getLSAction(),
    setAction: () => console.log('not implemented'),
    open: () => console.log('not implemented'),
    close: () => console.log('not implemented'),
    list: [],
});

interface IProps {
    app: ApplicationEnum;
    children?: ReactNode;
}

export function TutorialContextProvider({ app, children }: IProps) {
    const { isReady, pathname } = useRouter();
    const { restaurant } = useRestaurantContext();

    const [list, setList] = useState<ITutorial[]>([]);
    const [hasError, setHasError] = useState(false);

    const actionsRef = useRef<IAction>(getLSAction());
    const [actions, setActions] = useState<IAction>(getLSAction());

    const shownListRef = useRef<string[]>([]);
    const [shownList, setShownList] = useState<string[]>([]);

    const [selectedId, setSelectedId] = useState<string | null>(null);

    const setActionHandler = useCallback(
        (id: string, action: ActionEnum) => {
            if ([ActionEnum.Done, ActionEnum.Skip].includes(action)) {
                setSelectedId(null);
                shownListRef.current = uniq([...shownListRef.current, id]);
            }

            const clone = [...actionsRef.current.data];
            const idx = clone.findIndex((o) => o.id === id);
            if (idx > -1) {
                if (!clone[idx].actions.some((o) => o.action === action)) {
                    clone[idx].actions.push({
                        action,
                        createdAt: timeUnix(),
                    });
                }
            } else {
                clone.push({
                    id,
                    actions: [{ action, createdAt: timeUnix() }],
                });
            }
            actionsRef.current = {
                ...actions,
                data: clone,
            };
            setShownList(shownListRef.current);
            setActions(actionsRef.current);
            setLSAction(actionsRef.current);
        },
        [actions],
    );

    const openHandler = useCallback((id: string) => {
        setSelectedId(id);
    }, []);

    const closeHandler = useCallback(() => {
        setSelectedId(null);
    }, []);

    useEffect(() => {
        if (!isReady || !restaurant) {
            return;
        }

        TutorialAPIManager.getInstance()
            .getTutorial(app, restaurant.country_code, restaurant)
            .then((res) => {
                setList(orderBy(res, ['priority'], ['asc']));
            })
            .catch((error) => {
                console.error('Tutorial(fetch-types): ', error);
                setHasError(true);
            });
    }, [app, isReady, restaurant]);

    const tutorial = useMemo(() => {
        if (selectedId) {
            return list.find((item) => item.id === selectedId);
        }

        return list.find((item) => {
            if (shownList.includes(item.id)) {
                return false;
            }

            if (item.criteria?.pathnames?.length && !item.criteria.pathnames.includes(pathname)) {
                return false;
            }

            if (item.criteria?.actionTypes?.length) {
                const action = actions.data.find((o) => o.id === item.id);
                if (!action) {
                    return true;
                }
                return !item.criteria.actionTypes.some((o) => checkAction(action, o));
            }

            return true;
        });
    }, [list, pathname, actions, selectedId, shownList]);

    return (
        <TutorialContext.Provider
            value={{
                tutorial,
                hasError,
                actions,
                setAction: setActionHandler,
                open: openHandler,
                close: closeHandler,
                list,
            }}
        >
            {children}
        </TutorialContext.Provider>
    );
}

export const useTutorial = () => useContext(TutorialContext);
